export enum TaskStatus {
  TODO = "TODO",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
  CANCELLED = "CANCELLED",
}

export const TaskStatusLabels: Record<TaskStatus, string> = {
  [TaskStatus.TODO]: "À faire",
  [TaskStatus.IN_PROGRESS]: "En cours",
  [TaskStatus.DONE]: "Terminée",
  [TaskStatus.CANCELLED]: "Annulée",
};

export enum TaskUrgency {
  LOW = "LOW",
  NORMAL = "NORMAL",
  URGENT = "URGENT",
}

export const TaskUrgencyLabels: Record<TaskUrgency, string> = {
  [TaskUrgency.LOW]: "Basse",
  [TaskUrgency.NORMAL]: "Normale",
  [TaskUrgency.URGENT]: "Urgent",
};

export interface TaskAssignee {
  type: "employee" | "external";
  employee_id?: string;
  name?: string;
}

export interface TaskObjectLink {
  object_id: string;
  object_type: string;
  object_name?: string; // Enrichi par le backend si disponible
}

export function getAssigneeName(assignee: TaskAssignee, employees: any[]): string {
  if (assignee.type === "external") {
    return assignee.name || "";
  }
  if (assignee.type === "employee" && employees) {
    const emp = employees.find((e) => e.employee_id === assignee.employee_id);
    if (emp) {
      return `${emp.first_name} ${emp.last_name}`;
    }
  }
  return assignee.employee_id || "";
}

export class Task {
  task_id: string;
  eva_company_id: string;
  title: string;
  description: string;
  category_ids: string[]; // Multi-categories (remplace category_id)
  urgency: TaskUrgency;
  status: TaskStatus;
  assigned_to: TaskAssignee[];
  created_by: string;
  objects_links: TaskObjectLink[];
  phase_id: string;
  due_date: Date;
  planned_start_date: Date;
  actual_start_date: Date;
  completed_at: Date;
  is_private: boolean;
  created_at: Date;
  updated_at: Date;
  comments_count?: number;
  /** Commentaire saisi par le monteur (mobile) — affiché via une icône dans la liste */
  comment?: string;

  constructor() {
    this.task_id = "";
    this.eva_company_id = "";
    this.title = "";
    this.description = "";
    this.category_ids = [];
    this.urgency = TaskUrgency.NORMAL;
    this.status = TaskStatus.TODO;
    this.assigned_to = [];
    this.created_by = "";
    this.objects_links = [];
    this.phase_id = null;
    this.due_date = null;
    this.planned_start_date = null;
    this.actual_start_date = null;
    this.completed_at = null;
    this.is_private = false;
    this.created_at = null;
    this.updated_at = null;
  }

  static createInstance(data: any): Task {
    const t = Object.assign(new Task(), data);
    // Migration : support category_id (legacy) et category_ids (nouveau)
    if (data.category_ids) {
      t.category_ids = data.category_ids;
    } else if (data.category_id) {
      // Retro-compatibilite
      t.category_ids = data.category_id ? [data.category_id] : [];
    } else {
      t.category_ids = [];
    }
    if (data.due_date) t.due_date = new Date(data.due_date);
    if (data.planned_start_date) t.planned_start_date = new Date(data.planned_start_date);
    if (data.actual_start_date) t.actual_start_date = new Date(data.actual_start_date);
    if (data.completed_at) t.completed_at = new Date(data.completed_at);
    if (data.created_at) t.created_at = new Date(data.created_at);
    if (data.updated_at) t.updated_at = new Date(data.updated_at);
    if (data.comments_count) t.comments_count = data.comments_count;
    return t;
  }

  static createInstances(data: any[]): Task[] {
    return (data || []).map(Task.createInstance);
  }

  isOverdue(): boolean {
    if (!this.due_date) return false;
    if (this.status === TaskStatus.DONE || this.status === TaskStatus.CANCELLED) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(this.due_date);
    due.setHours(0, 0, 0, 0);
    // En retard seulement si la date est strictement avant aujourd'hui
    return due.getTime() < today.getTime();
  }
}
